import { useTranslation } from 'react-i18next';
import type { TimelineRouteItem } from '../../../../types/RoutineTimeline.types';
import {
  MetricIcon,
  MetricItem,
  ProgressCircle,
  ProgressCircleIcon,
  ProgressCircleWrap,
  StatusDot,
  StatusLabel,
  StatusRow,
} from '../RouteProgress.styles';
import {
  CleaningMeta,
  ItemContent,
  MetricsRow,
  ReportLink,
  RouteTitle,
  SecondaryText,
  SkipRouteButton,
  StatusIcon,
  TimelineItemRow,
  TimelineLine,
  TimelineRail,
  type TimelineTone,
} from './RoutineTimeline.styles';
import { PendingSpinnerIcon } from 'lib/components/PendingSpinnerIcon/PendingSpinnerIcon';
import { RobotBatteryPercentIcon } from 'app/modules/cleaning/widgets/robot-dashboard/components/RobotDashboard/RobotBatteryIcon/RobotBatteryIcon';
import { RobotWaterLevelIcon } from 'app/modules/cleaning/widgets/robot-dashboard/components/RobotDashboard/RobotWaterLevel/WaterLevelIcon';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import type { Optional } from 'lib/types/Optional';

interface TimelineItemProps {
  item: TimelineRouteItem;
  isLast: boolean;
  isPaused?: boolean;
  showSkip?: boolean;
  isSkipDisabled?: boolean;
  isSkipPending?: boolean;
  onSkipRoute?: () => void;
  onDownloadReport?: (item: TimelineRouteItem) => void;
  /** The report being exported, so its button shows as pending. */
  exportingReportId?: Optional<string>;
}

const getTone = (state: TimelineRouteItem['state']): TimelineTone => {
  switch (state) {
    case 'cancelled':
      return 'warning';
    case 'completed':
      return 'success';
    case 'cleaning':
      return 'cleaning';
    case 'stopped':
    case 'notCleaned':
      return 'muted';
    default:
      return 'upcoming';
  }
};

const getStatusIconName = (state: TimelineRouteItem['state']): 'warningNew' | 'check' | 'clock' | 'house' => {
  switch (state) {
    case 'cancelled':
      return 'warningNew';
    case 'completed':
      return 'check';
    case 'stopped':
      return 'house';
    default:
      return 'clock';
  }
};

export const TimelineItem = ({
  item,
  isLast,
  isPaused = false,
  showSkip = true,
  isSkipDisabled = false,
  isSkipPending = false,
  onSkipRoute,
  onDownloadReport,
  exportingReportId,
}: TimelineItemProps): JSX.Element => {
  const { t } = useTranslation();
  const tone = getTone(item.state);
  const isCleaningPaused = item.state === 'cleaning' && isPaused;

  return (
    <TimelineItemRow>
      <TimelineRail>
        {item.state === 'cleaning' ? (
          <ProgressCircleWrap>
            <ProgressCircle percent={item.progressPercent ?? 0} size={40} strokeWidth={8} />
            {isCleaningPaused && (
              <ProgressCircleIcon>
                <SvgIcon name="pauseThin" size={24} />
              </ProgressCircleIcon>
            )}
          </ProgressCircleWrap>
        ) : (
          <StatusIcon $tone={tone}>
            {item.state === 'finishing' ? (
              <PendingSpinnerIcon />
            ) : (
              <SvgIcon name={getStatusIconName(item.state)} size={24} />
            )}
          </StatusIcon>
        )}
        {!isLast && <TimelineLine $tone={tone} />}
      </TimelineRail>

      <ItemContent $isLast={isLast}>
        <RouteTitle>{item.name}</RouteTitle>

        {item.state === 'stopped' && <SecondaryText>{t('robotCleaningControl.liveMap.stopped')}</SecondaryText>}

        {item.state === 'notCleaned' && <SecondaryText>{t('robotCleaningControl.liveMap.notCleaned')}</SecondaryText>}

        {(item.state === 'cancelled' || item.state === 'completed') && item.metrics && (
          <MetricsRow>
            <MetricItem>
              <MetricIcon className="metric-icon--report">
                <SvgIcon name="flag" size={20} />
              </MetricIcon>
              {item.metrics.coveragePercent}%
            </MetricItem>
            <MetricItem>
              <MetricIcon className="metric-icon--report">
                <SvgIcon name="duration" size={20} />
              </MetricIcon>
              {t('robotCleaningControl.liveMap.minutesShort', { minutes: item.metrics.durationMinutes })}
            </MetricItem>
            {/* TODO: charging time after the route, until its source is decided the figure is not shown. */}
            {item.metrics.chargingMinutes !== undefined && (
              <MetricItem>
                <MetricIcon>
                  <SvgIcon name="house" size={20} />
                </MetricIcon>
                {t('robotCleaningControl.liveMap.minutesShort', { minutes: item.metrics.chargingMinutes })}
              </MetricItem>
            )}
            {item.state === 'cancelled' && <SecondaryText>{t('robotCleaningControl.liveMap.cancelled')}</SecondaryText>}
          </MetricsRow>
        )}

        {item.state === 'cleaning' && (
          <>
            <CleaningMeta>
              <StatusRow>
                <StatusDot />
                <StatusLabel>{t(isCleaningPaused ? 'robotDashboard.Paused' : 'robotDashboard.Cleaning')}</StatusLabel>
              </StatusRow>
              {item.minutesLeft !== undefined && (
                <SecondaryText>
                  {t('robotCleaningControl.liveMap.minutesLeft', { count: item.minutesLeft })}
                </SecondaryText>
              )}
            </CleaningMeta>
            <MetricsRow>
              <MetricItem>
                <MetricIcon className="metric-icon--battery">
                  <RobotBatteryPercentIcon batteryPercentageNumber={item.batteryPercent ?? 0} size={20} />
                </MetricIcon>
                {item.batteryPercent}%
              </MetricItem>
              <MetricItem>
                <MetricIcon>
                  <RobotWaterLevelIcon waterPercent={item.waterPercent ?? 0} size={20} />
                </MetricIcon>
                {t('robotCleaningControl.liveMap.waterLiters', { liters: item.waterLiters })}
              </MetricItem>
            </MetricsRow>
            {showSkip && (
              <SkipRouteButton
                disabled={isSkipDisabled}
                icon={isSkipPending ? <PendingSpinnerIcon /> : <SvgIcon name="skip" size={20} />}
                onClick={onSkipRoute}
              >
                {t('robotCleaningControl.skipRoute.label')}
              </SkipRouteButton>
            )}
          </>
        )}

        {item.state === 'upcoming' && item.estimatedDuration && (
          <SecondaryText>
            {t('robotCleaningControl.liveMap.estimatedDuration', { duration: item.estimatedDuration })}
          </SecondaryText>
        )}

        {item.isReportUnavailable && (
          <SecondaryText>{t('robotCleaningControl.liveMap.reportUnavailable')}</SecondaryText>
        )}

        {(item.state === 'cancelled' || item.state === 'completed') && item.reportId && onDownloadReport && (
          <ReportLink type="button" disabled={!!exportingReportId} onClick={(): void => onDownloadReport(item)}>
            {exportingReportId === item.reportId ? <PendingSpinnerIcon /> : <SvgIcon name="download" size={20} />}
            {t('robotCleaningControl.liveMap.downloadReport')}
          </ReportLink>
        )}
      </ItemContent>
    </TimelineItemRow>
  );
};
